import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import {
  PoComboFilter,
  PoComboOption,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoSelectOption,
  PoTableAction,
  PoTableColumn,
  PoTableColumnLabel,
} from '@po-ui/ng-components';
import { Issue, IssueFilters, IssueSeverity, IssueGoal, IssueStatus, IssueType } from '../../interfaces/issue';
import { Project } from '../../interfaces/project';
import { Release } from '../../interfaces/release';
import { SonarExtractorService } from '../../services/sonar-extractor.service';
import { formatDateTime, triggerDownload } from '../../utils/sonar-extractor.utils';

@Component({
  selector: "app-sonar-extractor-issues",
  templateUrl: "./sonar-extractor-issues.component.html",
  styleUrls: ["./sonar-extractor-issues.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SonarExtractorIssuesComponent implements OnInit {
  @ViewChild("modalDetail", { static: true }) modalDetail!: PoModalComponent;

  issues: Issue[] = [];
  loading = false;
  exportingCsv = false;
  selectedIssue: Issue | null = null;
  detailItems: { label: string; value: string }[] = [];

  // Paginação
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  // Filtros
  filterGoal: IssueGoal | 'ALL' | null = null;
  filterProjectKey: string | null = null;
  filterReleaseId: number | 'ALL' | null = null;
  filterPo: string | null = null;
  filterRuleKey = "";
  filterSquad: string | null = null;
  filterModule: string | null = null;
  filterTribe: string | null = null;

  // Lista completa de projetos para derivar opções dos filtros de PO / Squad / Módulo / Tribe
  private allProjects: Project[] = [];

  projectOptions: PoSelectOption[] = [{ label: 'Todos', value: 'ALL' }];
  releaseOptions: PoSelectOption[] = [{ label: 'Todas', value: 'ALL' }];
  poOptions: PoSelectOption[] = [{ label: 'Todos', value: 'ALL' }];
  squadOptions: PoSelectOption[] = [{ label: 'Todos', value: 'ALL' }];
  moduleOptions: PoSelectOption[] = [{ label: 'Todos', value: 'ALL' }];
  tribeOptions: PoSelectOption[] = [{ label: 'Todas', value: 'ALL' }];

  readonly goalOptions: PoSelectOption[] = [
    { label: "Todos",                      value: "ALL" },
    { label: "Zerar",                      value: "RESOLVE" },
    { label: "Zerar ou ao menos reduzir",  value: "RESOLVE_OR_REDUCE" },
  ];

  // Filter services para po-combo — closures sobre os arrays de opções,
  // lidos no momento da chamada (arrays são populados assincronamente).
  readonly goalFilterService    = this.createLocalFilter(() => this.goalOptions);
  readonly projectFilterService = this.createLocalFilter(() => this.projectOptions);
  readonly releaseFilterService = this.createLocalFilter(() => this.releaseOptions);
  readonly poFilterService      = this.createLocalFilter(() => this.poOptions);
  readonly squadFilterService   = this.createLocalFilter(() => this.squadOptions);
  readonly moduleFilterService  = this.createLocalFilter(() => this.moduleOptions);
  readonly tribeFilterService   = this.createLocalFilter(() => this.tribeOptions);

  readonly tableColumns: PoTableColumn[] = [
    { property: "release_name", label: "Release",     width: "100px"                   },
    { property: "rule_key",     label: "Rule Key",     width: "100px"                   },
    { property: "rule_name",    label: "Regra",        width: "200px"                   },
    {
      property: "goal",
      label: "Premissa",
      width: "180px",
      type: 'columnTemplate',
    },
    { property: "project_name", label: "Projeto",      width: "100px"                   },
    { property: "po",           label: "PO",           width: "100px"                   },
    { property: "squad",        label: "Squad",        width: "100px"                   },
    { property: "module",       label: "Módulo",       width: "100px"                   },
    { property: "tribe",        label: "Tribe",        width: "100px"                   },
    { property: "segment",      label: "Segmento",     width: "100px", visible: false   },
    { property: "area",         label: "Área",         width: "100px", visible: false   },
  ];

  readonly tableActions: PoTableAction[] = [
    {
      label: "Ver Detalhes",
      icon: "po-icon-eye",
      action: (issue: Issue) => this.openDetailModal(issue),
    },
  ];

  modalDetailCloseAction: PoModalAction = {
    label: "Fechar",
    action: () => this.modalDetail.close(),
  };

  constructor(
    private sonarService: SonarExtractorService,
    private notification: PoNotificationService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.loadProjects();
    this.loadReleases();
    // Adia para após o primeiro ciclo de change detection do Angular,
    // evitando NG0100 (ExpressionChangedAfterItHasBeenCheckedError)
    // causado pelo po-table tentar renderizar o loading overlay durante ngOnInit.
    setTimeout(() => this.loadIssues());
  }

  // ── Carregamento de dados ────────────────────────────────────────────────

  loadProjects(): void {
    // Carrega os projetos (até 1000, em 2 requests paralelas) para derivar as
    // opções de Projeto, PO, Squad, Módulo e Tribe.
    this.sonarService.getAllProjects()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (projects: Project[]) => {
        this.allProjects    = Array.isArray(projects) ? projects : [];
        this.projectOptions = [
          { label: 'Todos', value: 'ALL' },
          ...this.allProjects.map(p => ({ label: p.name, value: p.sonar_key })),
        ];
        this.poOptions     = this.buildDistinctOptions(this.allProjects, 'po',     'Todos');
        this.squadOptions  = this.buildDistinctOptions(this.allProjects, 'squad',  'Todos');
        this.moduleOptions = this.buildDistinctOptions(this.allProjects, 'module', 'Todos');
        this.tribeOptions  = this.buildDistinctOptions(this.allProjects, 'tribe',  'Todas');
        this.cdr.markForCheck();
      },
      error: () => this.notification.error("Erro ao carregar a lista de projetos."),
    });
  }

  loadReleases(): void {
    this.sonarService.getReleases({ page: 1, limit: 1000 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (result) => {
        const list: Release[] = Array.isArray(result?.releases) ? result.releases : [];
        this.releaseOptions = [
          { label: 'Todas', value: 'ALL' },
          ...list.map((r) => ({ label: r.name, value: r.id })),
        ];
        this.cdr.markForCheck();
      },
      error: () => this.notification.error('Erro ao carregar a lista de releases.'),
    });
  }

  loadIssues(append = false): void {
    this.loading = true;
    const filters: IssueFilters = {
      page:       this.currentPage,
      limit:      this.itemsPerPage,
      goal:       this.isAllFilter(this.filterGoal)       ? undefined : this.filterGoal as IssueGoal,
      projectKey: this.isAllFilter(this.filterProjectKey) ? undefined : this.filterProjectKey!,
      releaseId:  this.isAllFilter(this.filterReleaseId)  ? undefined : this.filterReleaseId as number,
      po:         this.isAllFilter(this.filterPo)         ? undefined : this.filterPo!,
      ruleKey:    this.filterRuleKey || undefined,
      squad:      this.isAllFilter(this.filterSquad)      ? undefined : this.filterSquad!,
      module:     this.isAllFilter(this.filterModule)     ? undefined : this.filterModule!,
      tribe:      this.isAllFilter(this.filterTribe)      ? undefined : this.filterTribe!,
    };

    this.sonarService.getIssues(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (result) => {
        this.issues     = append ? [...this.issues, ...result.issues] : result.issues;
        this.totalItems = result.total;
        this.loading    = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notification.error("Erro ao carregar as issues.");
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ── Ações dos filtros ────────────────────────────────────────────────────

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadIssues();
  }

  clearFilters(): void {
    this.filterGoal       = null;
    this.filterProjectKey = null;
    this.filterReleaseId  = null;
    this.filterPo         = null;
    this.filterRuleKey    = '';
    this.filterSquad      = null;
    this.filterModule     = null;
    this.filterTribe      = null;
    this.currentPage = 1;
    this.loadIssues();
  }

  onShowMore(): void {
    this.currentPage++;
    this.loadIssues(true);
  }

  exportCsv(): void {
    this.exportingCsv = true;
    const filters: IssueFilters = {
      goal:       this.isAllFilter(this.filterGoal)       ? undefined : this.filterGoal as IssueGoal,
      projectKey: this.isAllFilter(this.filterProjectKey) ? undefined : this.filterProjectKey!,
      releaseId:  this.isAllFilter(this.filterReleaseId)  ? undefined : this.filterReleaseId as number,
      po:         this.isAllFilter(this.filterPo)         ? undefined : this.filterPo!,
      ruleKey:    this.filterRuleKey || undefined,
      squad:      this.isAllFilter(this.filterSquad)      ? undefined : this.filterSquad!,
      module:     this.isAllFilter(this.filterModule)     ? undefined : this.filterModule!,
      tribe:      this.isAllFilter(this.filterTribe)      ? undefined : this.filterTribe!,
    };

    this.sonarService.exportIssuesCsv(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: blob => {
        triggerDownload(blob, 'issues.csv');
        this.exportingCsv = false;
      },
      error: () => {
        this.notification.error('Erro ao exportar as issues em CSV.');
        this.exportingCsv = false;
      },
    });
  }

  openDetailModal(issue: Issue): void {
    this.selectedIssue = issue;
    this.detailItems = this.buildDetailItems(issue);
    this.cdr.markForCheck();
    this.modalDetail.open();
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Retorna true quando o valor de filtro representa "todos" — ou seja,
   * null (estado inicial/limpo) ou 'ALL' (opção "Todos" selecionada explicitamente).
   */
  private isAllFilter(value: unknown): boolean {
    return value === null || value === undefined || value === 'ALL';
  }

  /**
   * Cria um PoComboFilter que filtra localmente sobre um array de opções.
   * Usar p-filter-service no lugar de p-options evita que o po-combo processe
   * e renderize a lista inteira a cada ciclo de change detection.
   *
   * - getFilteredData: retorna todas as opções quando o texto está vazio
   *   (comportamento do primeiro clique) e filtra por "contains" ao digitar.
   * - getObjectByValue: resolve o label de um valor já selecionado sem HTTP.
   */
  private createLocalFilter(getOptions: () => PoSelectOption[]): PoComboFilter {
    return {
      getFilteredData(params: { property?: string; value?: string }): Observable<PoComboOption[]> {
        const search = (params.value ?? '').toString().toLowerCase().trim();
        const options = getOptions();
        const result = search
          ? options.filter(opt =>
              (opt.label ?? String(opt.value)).toLowerCase().includes(search)
            )
          : options;
        return of(result as PoComboOption[]);
      },
      getObjectByValue(value: string | number): Observable<PoComboOption> {
        const found = getOptions().find(opt => opt.value === value);
        return of(found ? (found as PoComboOption) : ({ value } as PoComboOption));
      },
    };
  }

  /**
   * Extrai valores distintos não-vazios de um campo dos projetos e retorna
   * como PoSelectOption[], ordenados alfabeticamente.
   */
  private buildDistinctOptions(
    projects: Project[],
    field: keyof Project,
    allLabel: string
  ): PoSelectOption[] {
    const values = [
      ...new Set(
        projects
          .map(p => (p[field] as string || '').trim())
          .filter(v => v.length > 0)
      ),
    ].sort((a, b) => a.localeCompare(b));

    return [
      { label: allLabel, value: 'ALL' },
      ...values.map(v => ({ label: v, value: v })),
    ];
  }

  buildDetailItems(issue: Issue): { label: string; value: string }[] {
    return [
      { label: "ID",                    value: issue.id?.toString() ?? "-" },
      { label: "Release",               value: issue.release_name?.toString() ?? "-" },
      { label: "Segmento",              value: issue.segment?.toString() ?? "-" },
      { label: "Área",                  value: issue.area?.toString() ?? "-" },
      { label: "Squad",                 value: issue.squad?.toString() ?? "-" },
      { label: "Módulo",                value: issue.module?.toString() ?? "-" },
      { label: "Tribo",                 value: issue.tribe?.toString() ?? "-" },
      { label: "PO",                    value: issue.po?.toString() ?? "-" },
      { label: "Premissa da regra",     value: this.getGoalLabel(issue.goal) },
      { label: "Projeto",               value: issue.project_name ?? "-" },
      { label: "Chave Sonar",           value: issue.sonar_key ?? "-" },
      { label: "Regra (chave)",         value: issue.rule_key ?? "-" },
      { label: "Regra (nome)",          value: issue.rule_name ?? "-" },
      { label: "Severidade",            value: this.getSeverityLabel(issue.severity) },
      { label: "Tipo",                  value: this.getTypeLabel(issue.type) },
      { label: "Status",                value: this.getStatusLabel(issue.status) },
      { label: "Resolução",             value: issue.resolution || "-" },
      { label: "Componente",            value: issue.component ?? "-" },
      { label: "Linha",                 value: issue.line?.toString() ?? "-" },
      { label: "Esforço",               value: issue.effort || "-" },
      { label: "Dívida técnica",        value: issue.debt || "-" },
      { label: "Criado em (Sonar)",     value: formatDateTime(issue.sonar_created_at) },
      { label: "Atualizado em (Sonar)", value: formatDateTime(issue.sonar_updated_at) },
      { label: "Importado em",          value: formatDateTime(issue.created_at) },
    ];
  }

  getSeverityLabel(severity: IssueSeverity): string {
    const map: Record<IssueSeverity, string> = {
      BLOCKER:  "Blocker",
      CRITICAL: "Critical",
      MAJOR:    "Major",
      MINOR:    "Minor",
      INFO:     "Info",
    };
    return map[severity] ?? severity;
  }

  getGoalLabel(goal: IssueGoal): string {
    const map: Record<IssueGoal, string> = {
      RESOLVE:          "Zerar",
      RESOLVE_OR_REDUCE: "Zerar ou ao menos reduzir",
    };
    return map[goal] ?? goal;
  }

  getTypeLabel(type: IssueType): string {
    const map: Record<IssueType, string> = {
      BUG:           "Bug",
      VULNERABILITY: "Vulnerability",
      CODE_SMELL:    "Code Smell",
    };
    return map[type] ?? type;
  }

  getStatusLabel(status: IssueStatus): string {
    const map: Record<IssueStatus, string> = {
      OPEN:      "Aberta",
      CONFIRMED: "Confirmada",
      REOPENED:  "Reaberta",
      RESOLVED:  "Resolvida",
      CLOSED:    "Fechada",
    };
    return map[status] ?? status;
  }
}
